const BEACON_FOR_COMPANY = [
    '[company]',
    '[compagnie]',
    '[enterprise]',
    '[entreprise]',
    '[organization]',
] as const;

const BEACON_FOR_MANAGER_LABEL = ['[manager]', '[gestionnaire]'] as const;

// Aliases (EN + FR). FR includes long/short forms with straight + curly apostrophe
// variants to tolerate copy-paste from different editors.
const BEACON_FOR_EMPLOYEE_FIRST_NAME = [
    '[employee name]',
    "[nom de l'employé]",
    '[nom de l’employé]',
    "[nom d'employé]",
    '[nom d’employé]',
] as const;

// A generated manager-assessment fallback question names the competency or company value it is about.
// The backend emits that name as a tag rather than baking it into the sentence, so the name resolves
// in the reader's language independently of the sentence around it: an owner with no French name used
// to leave a gap in an otherwise complete French sentence, which no language fallback could repair
// (#15386).
const BEACON_FOR_COMPETENCY_NAME = [
    '[competency name]',
    '[nom de la compétence]',
    '[nom de la competence]',
] as const;

const BEACON_FOR_COMPANY_VALUE_NAME = [
    '[value name]',
    "[nom de la valeur d'entreprise]",
    '[nom de la valeur d’entreprise]',
    '[nom de la valeur]',
] as const;

interface ReplaceDynamicTagsInput {
    text: string | undefined | null;
    companyName?: string;
    managerLabel?: string;
    employeeFirstName?: string;
    competencyName?: string;
    companyValueName?: string;
}

const buildBeaconRegex = (beacons: ReadonlyArray<string>): RegExp => {
    const escaped = beacons.map((term) => term.replace(/[[\]]/g, '\\$&')).join('|');
    return new RegExp(escaped, 'gi');
};

const COMPANY_REGEX = buildBeaconRegex(BEACON_FOR_COMPANY);
const MANAGER_REGEX = buildBeaconRegex(BEACON_FOR_MANAGER_LABEL);
const EMPLOYEE_FIRST_NAME_REGEX = buildBeaconRegex(BEACON_FOR_EMPLOYEE_FIRST_NAME);
const COMPETENCY_NAME_REGEX = buildBeaconRegex(BEACON_FOR_COMPETENCY_NAME);
const COMPANY_VALUE_NAME_REGEX = buildBeaconRegex(BEACON_FOR_COMPANY_VALUE_NAME);

const replaceDynamicTags = ({
    text,
    companyName,
    managerLabel,
    employeeFirstName,
    competencyName,
    companyValueName,
}: ReplaceDynamicTagsInput): string => {
    // Guard: ensure we only operate on strings
    if (typeof text !== 'string' || text.length === 0) {
        return '';
    }

    let output = text;
    // Function-form replacer avoids `$&`, `$1`, `$'` interpretation when a value contains `$`.
    if (companyName) {
        output = output.replace(COMPANY_REGEX, () => companyName);
    }
    if (managerLabel) {
        output = output.replace(MANAGER_REGEX, () => managerLabel);
    }
    if (employeeFirstName) {
        output = output.replace(EMPLOYEE_FIRST_NAME_REGEX, () => employeeFirstName);
    }
    if (competencyName) {
        output = output.replace(COMPETENCY_NAME_REGEX, () => competencyName);
    }
    if (companyValueName) {
        output = output.replace(COMPANY_VALUE_NAME_REGEX, () => companyValueName);
    }
    return output;
};

export {
    BEACON_FOR_COMPANY,
    BEACON_FOR_MANAGER_LABEL,
    BEACON_FOR_EMPLOYEE_FIRST_NAME,
    BEACON_FOR_COMPETENCY_NAME,
    BEACON_FOR_COMPANY_VALUE_NAME,
    replaceDynamicTags,
};
export type { ReplaceDynamicTagsInput };
